import { Container, Text, Sprite, Graphics } from "pixi.js";
import { ISocialsThemeRendererProps } from "./ISocialsThemeRenderer";

export const renderTheme4 = ({
  container,
  destination,
  integrations,
  widget,
  icons,
  scaleFactor,
}: ISocialsThemeRendererProps) => {
  const options = widget.options;
  const itemsContainer = new Container();

  let currentX = 0;
  const gap = 16 * scaleFactor;

  integrations.forEach((data) => {
    const item = new Container();

    // Icon Size
    const iconSize =
      (options.fontSize === "xs"
        ? 20
        : options.fontSize === "sm"
          ? 24
          : options.fontSize === "md"
            ? 28
            : options.fontSize === "lg"
              ? 32
              : 36) * scaleFactor;

    // Text setup first to measure width
    const isLightBg = data.color === "#ffffff" || data.color === "#fff";
    const textColor = isLightBg ? "#000000" : "#ffffff";

    const textStyle = {
      fontFamily: options.fontFamily || "Roboto",
      fontSize:
        (options.fontSize === "xs"
          ? 14
          : options.fontSize === "sm"
            ? 16
            : options.fontSize === "md"
              ? 18
              : options.fontSize === "lg"
                ? 20
                : 22) * scaleFactor,
      fill: textColor,
      fontWeight: options.bold ? "bold" : "normal",
      fontStyle: options.italic ? "italic" : "normal",
      letterSpacing: options.wordSpacing,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any;

    const displayName = data.user?.displayName || `@${data.platform}`;
    const text = new Text({ text: displayName, style: textStyle });
    text.anchor.set(0, 0.5);

    // Padding
    const px = 24 * scaleFactor;
    const py = 12 * scaleFactor;
    const innerGap = 12 * scaleFactor;

    // Total size
    const totalWidth = px + iconSize + innerGap + text.width + px;
    const totalHeight = Math.max(iconSize, text.height) + py * 2;

    // Pill BG
    const bg = new Graphics();
    bg.roundRect(0, 0, totalWidth, totalHeight, totalHeight / 2).fill(data.color);
    item.addChild(bg);

    // Icon
    const iconTexture = icons[data.platform];
    if (iconTexture) {
      const icon = new Sprite(iconTexture);
      icon.width = iconSize;
      icon.height = iconSize;
      icon.anchor.set(0, 0.5);
      icon.x = px;
      icon.y = totalHeight / 2;
      // No tint for this theme usually, or maybe black/white?
      // Preview says: <div style={{ fontSize: sizes.icon }}><IconComp /></div>
      // Icons seem to be colored SVG/PNGs. If PNGs are black/white mask, we need to know.
      // Assuming PNGs are the official logos.
      // Theme 1 used tint for platform color.
      // Theme 4 uses platform color for BG.
      // So Icon should probably be White or Original?
      // Preview uses `IconComp`.
      // `getIconComponent` returns `img src=...`.
      // It seems the icons are the logos provided in assets. `twitch-logo.png` is purple?
      // If `twitch-logo.png` is purple, and BG is purple, it won't show.
      // Wait, `SOCIAL_PLATFORMS` defined colors.
      // Theme 1: `style={{ color: integration.color }}`.
      // Theme 4: `style={{ backgroundColor: integration.color }}`.
      // If I use the icon as is, and it's the logo, does it contrast?
      // In Theme 4 preview, the text color flips relative to BG lightness.
      // The icon color isn't explicitly set in Theme 4 preview code. It inherits? No, `div style={{ fontSize... }}`.
      // If the PNG is colored, it might clash.
      // If I assume `Mask` behavior like Theme 3 (White Icon), I should tint it white/black.
      // But `twitch-logo.png` is likely purple.
      // Let's assume we need to tint it based on contrast, similar to text.
      // icon.tint = textColor; // Tint icon same as text // REMOVED to match React Preview behavior (Original Logo)
      item.addChild(icon);
    }

    text.x = px + iconSize + innerGap;
    text.y = totalHeight / 2;
    item.addChild(text);

    if (options.underline) {
      const underline = new Graphics();
      underline.moveTo(0, 0);
      underline.lineTo(text.width, 0);
      underline.stroke({ width: 2 * scaleFactor, color: textColor });
      underline.x = text.x;
      underline.y = text.y + text.height / 2;
      item.addChild(underline);
    }

    if (options.strike) {
      const strike = new Graphics();
      strike.moveTo(0, 0);
      strike.lineTo(text.width, 0);
      strike.stroke({ width: 2 * scaleFactor, color: textColor });
      strike.x = text.x;
      strike.y = text.y;
      item.addChild(strike);
    }

    item.x = currentX;
    itemsContainer.addChild(item);

    currentX += totalWidth + gap;
  });

  itemsContainer.x = (destination.width - itemsContainer.width) / 2;
  itemsContainer.y = (destination.height - itemsContainer.height) / 2;

  container.addChild(itemsContainer);
};
